import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Panel } from '/imports/ui/Panel';
import { Badge, BadgeTone } from '/imports/ui/Badge';
import { Icon } from '/imports/ui/Icon';
import { LoadingFlex } from '/imports/ui/LoadingFlex';
import { useMethod } from '/imports/hooks';
import {
  getTradeInspection, getCounterpartyTrades, getTradeProfitLifo,
  TradeCardLine, TradeInspection as Inspection, TradeProfitLifo, TradeSessionRow,
} from '/imports/api/analytics';
import {
  kindVocab, INTENT_TONE, DIR_LABEL, RARITY_COLOR,
  fmtInt, fmtTix, fmtSigned, fmtPrice, fmtPct, hhmm, hhmmss, ageLabel, ymd,
} from '/imports/ui/tradeVocab';

interface Props {
  id: string;
  onBack: () => void;
  onOpen: (id: string) => void;
  onFilterPartner: (partnerId: number, name: string) => void;
}

/**
 * One trade in full: who, how it ended, the trade window rebuilt from the
 * ledger, the lifecycle, the raw flags, and this partner's whole timeline.
 */
export const TradeInspection = ({ id, onBack, onOpen, onFilterPartner }: Props) => {
  const [insp, loading] = useMethod(getTradeInspection, { id }, [id]);
  const partnerId = insp?.row.partnerId ?? 0;
  const [history] = useMethod(getCounterpartyTrades, { partnerId }, [partnerId], () => partnerId > 0);

  if (loading) return <LoadingFlex />;
  if (!insp) {
    return (
      <Panel>
        <div className="flex flex-col items-center gap-3 py-16 text-center">
          <div className="text-[15px] font-semibold text-fg-1">Trade not found</div>
          <button type="button" onClick={onBack} className="cursor-pointer rounded border-none bg-transparent text-[13px] text-primary hover:underline">Back to the Trade Inspector</button>
        </div>
      </Panel>
    );
  }
  const { row, counterparty: c } = insp;
  const k = kindVocab(row.kind);

  return (
    <div className="flex flex-col gap-3 pb-6">
      <button type="button" onClick={onBack} className="inline-flex cursor-pointer items-center gap-1.5 self-start border-none bg-transparent p-0 text-[13px] font-semibold text-fg-3 hover:text-fg-1">
        <Icon icon="chevronLeft" size={15} /> Trade inspector
      </button>

      {/* who + outcome + their standing with us */}
      <Panel flush>
        <div className="flex flex-wrap items-start gap-4 p-4 sm:p-5">
          <Monogram name={row.partner} tone={INTENT_TONE[row.intent]} />
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <h2 className="m-0 text-[22px] font-bold leading-none text-fg-1">
                <Link to={`/musers/${row.partnerId}`} className="text-fg-1 no-underline hover:underline">{row.partner}</Link>
              </h2>
              <Badge tone={INTENT_TONE[row.intent]}>{row.intent}</Badge>
              {c.isNew && <Badge tone="green">new</Badge>}
              {c.profit < 0 && <Badge tone="red">costly</Badge>}
              {row.partnerIsBot != null && row.partnerIsBot >= 0.5 && <Badge tone="blue" title={insp.partnerBotSignals.join(', ')}>bot {Math.round(row.partnerIsBot * 100)}%</Badge>}
              {row.initiatedByUs && <Badge tone="gold">we invited</Badge>}
            </div>
            <p className="mt-1.5 text-[13px] text-fg-3">
              {ageLabel(row.at)} · {hhmm(row.at)} · on {insp.botDocId ? <Link to={`/bots/${insp.botDocId}`} className="text-fg-2">{row.bot}</Link> : row.bot}
              {row.tradeno ? <> · trade <span className="num">{row.tradeno}</span></> : null}
              {insp.escrowId ? <> · <Link to={`/escrows/${insp.escrowId}`} className="text-fg-3">escrow</Link></> : null}
            </p>
            <button type="button" onClick={() => onFilterPartner(row.partnerId, row.partner)} className="mt-2 inline-flex cursor-pointer items-center gap-1 border-none bg-transparent p-0 text-[12px] font-semibold text-primary hover:underline">
              See all of {row.partner}&rsquo;s trades <Icon icon="chevronRight" size={13} />
            </button>
          </div>
          <span className={`inline-flex shrink-0 items-center gap-1.5 rounded-lg border px-3 py-1.5 text-[14px] font-bold ${outcomeClass[k.tone]}`}>
            <Icon icon={row.completed ? 'check' : 'close'} size={15} />
            {row.creditPayout ? 'Credit payout' : k.label}
          </span>
        </div>
        <div className="grid grid-cols-2 divide-x divide-y divide-soft border-t border-soft sm:grid-cols-3 sm:divide-y-0 lg:grid-cols-6">
          <Cell label="Opens with us" value={fmtInt(c.opens)} sub="since sessions began" />
          <Cell label="Completed" value={fmtInt(c.completes)} sub={c.opens ? `${fmtPct((c.completes / c.opens) * 100)} rate` : undefined} />
          <Cell label="First traded" value={c.firstTrade ? ageLabel(c.firstTrade) : c.firstSeen ? ageLabel(c.firstSeen) : '–'} sub={c.firstTrade ? ymd(c.firstTrade) : undefined} />
          <Cell label="Last seen" value={c.lastSeen ? ageLabel(c.lastSeen) : '–'} sub={c.lastSeen ? ymd(c.lastSeen) : undefined} />
          <Cell label="Our profit" value={c.profitUnits ? fmtSigned(c.profit) : 'none'} tone={c.profit > 0 ? 'up' : c.profit < 0 ? 'down' : undefined} sub={c.profitUnits ? `${fmtInt(c.profitUnits)} sold, held cost` : 'nothing sold to them'} />
          <ProfitCell id={id} insp={insp} />
        </div>
      </Panel>

      <p className="m-0 max-w-2xl text-[14px] leading-snug text-fg-2">{insp.summary}</p>

      <TradeWindow insp={insp} />
      <Stepper stages={insp.stages} tone={k.tone} />
      <Flags insp={insp} />
      <HistoryPanel current={id} partner={row.partner} rows={history?.rows ?? []} total={history?.total ?? 0} onOpen={onOpen} />
    </div>
  );
};

const outcomeClass: Record<BadgeTone, string> = {
  green: 'border-primary/40 bg-primary/15 text-primary',
  amber: 'border-warn/40 bg-warn/15 text-warn',
  red: 'border-sell/40 bg-sell/15 text-sell',
  blue: 'border-info/40 bg-info/15 text-info',
  gold: 'border-gold/40 bg-gold/15 text-gold',
  neutral: 'border-soft bg-page-alt text-fg-2',
};

const Monogram = ({ name, tone }: { name: string; tone: BadgeTone }) => {
  const initials = (name.replace(/[^a-zA-Z0-9]/g, '').slice(0, 2) || '??').toUpperCase();
  return <div className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-xl border text-[15px] font-bold tracking-tight ${outcomeClass[tone]}`}>{initials}</div>;
};

const Cell = ({ label, value, sub, tone }: { label: string; value: React.ReactNode; sub?: string; tone?: 'up' | 'down' }) => (
  <div className="px-4 py-3">
    <div className="eyebrow text-fg-3">{label}</div>
    <div className={`num mt-1 text-[16px] font-bold ${tone === 'up' ? 'text-buy' : tone === 'down' ? 'text-sell' : 'text-fg-1'}`}>{value}</div>
    {sub && <div className="mt-0.5 text-[11px] text-fg-3">{sub}</div>}
  </div>
);

/**
 * This trade's realized profit. The default is the held cost the money rule
 * floors on (cheap, an as-of-now average); the button walks the whole ledger
 * for the exact LIFO lots this sale consumed and swaps the value in.
 */
const ProfitCell = ({ id, insp }: { id: string; insp: Inspection }) => {
  const [lifo, setLifo] = useState<TradeProfitLifo | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const held = insp.profit;
  const walk = async () => {
    setBusy(true);
    setErr(null);
    try { setLifo(await getTradeProfitLifo.call({ id })); }
    catch (e: any) { setErr(e?.reason ?? e?.message ?? 'failed'); }
    finally { setBusy(false); }
  };
  const value = lifo ? lifo.profit : held?.value ?? 0;
  const sold = held?.units ?? 0;
  return (
    <div className="px-4 py-3">
      <div className="flex items-center justify-between gap-2">
        <div className="eyebrow text-fg-3">This trade</div>
        {sold > 0 && !lifo && (
          <button type="button" onClick={walk} disabled={busy} title="Walk the whole ledger for the exact lots this sale consumed" className="inline-flex cursor-pointer items-center gap-1 rounded border border-soft bg-transparent px-1.5 py-0.5 text-[10px] font-semibold text-fg-3 hover:border-mid hover:text-fg-1 disabled:cursor-wait">
            <Icon icon="layers" size={11} /> {busy ? 'walking…' : 'exact LIFO'}
          </button>
        )}
      </div>
      <div className={`num mt-1 text-[16px] font-bold ${sold ? (value > 0 ? 'text-buy' : value < 0 ? 'text-sell' : 'text-fg-1') : 'text-fg-2'}`}>
        {sold ? fmtSigned(value) : insp.row.completed ? 'none' : '–'}
      </div>
      <div className="mt-0.5 text-[11px] text-fg-3">
        {err ? <span className="text-sell">{err}</span>
          : lifo ? `exact LIFO · cost ${fmtPrice(lifo.cogs)}${lifo.unmatchedUnits ? ` · ${fmtInt(lifo.unmatchedUnits)} unmatched` : ''}`
            : sold ? `held cost${held?.estimatedUnits ? ` · ${fmtInt(held.estimatedUnits)} est.` : ''}${held?.unknownUnits ? ` · ${fmtInt(held.unknownUnits)} uncosted` : ''}`
              : 'nothing sold'}
      </div>
    </div>
  );
};

const statusTone: Record<TradeCardLine['status'], BadgeTone> = { bought: 'green', wanted: 'amber', 'not bought': 'amber' };

const CardTile = ({ l }: { l: TradeCardLine }) => (
  <div className={`flex items-stretch gap-2.5 rounded-lg border px-2 py-2 ${l.status === 'bought' ? 'border-primary/25 bg-primary/10' : 'border-warn/25 bg-warn/10 opacity-80'}`}>
    <span className="w-1 shrink-0 self-stretch rounded-full" style={{ background: RARITY_COLOR[l.rarity] ?? RARITY_COLOR.COMMON }} />
    <div className="min-w-0 flex-1 self-center">
      <div className="flex min-w-0 items-center gap-1.5 text-[13px] leading-tight text-fg-1">
        <Link to={`/cards/${l.cardId}`} className="truncate text-fg-1 no-underline hover:underline">{l.card}</Link>
        {l.foil && <Badge tone="gold" title="foil">F</Badge>}
      </div>
      <div className="eyebrow mt-0.5 text-[10px] text-fg-3">{l.set}</div>
    </div>
    <div className="shrink-0 self-center text-right">
      <div className="num text-[12px] text-fg-2">{l.qty}x</div>
      <div className="num text-[10px] text-fg-3" title={l.bookPrice ? 'our book price today, not what was filed' : 'filed price'}>{fmtPrice(l.price)}{l.bookPrice ? '*' : ''}</div>
    </div>
    <span className="shrink-0 self-center"><Badge tone={statusTone[l.status]}>{l.status}</Badge></span>
  </div>
);

/** The tix that changed hands, as a row matching the card tiles. */
const TicketTile = ({ count, sub, status, tone }: { count: number; sub: string; status: string; tone: BadgeTone }) => (
  <div className="flex items-stretch gap-2.5 rounded-lg border border-gold/30 bg-gold/10 px-2 py-2">
    <span className="w-1 shrink-0 self-stretch rounded-full bg-gold" />
    <span className="self-center"><Icon icon="coins" size={18} className="text-gold" /></span>
    <div className="min-w-0 flex-1 self-center">
      <div className="text-[13px] leading-tight text-fg-1">Event Tickets</div>
      <div className="eyebrow mt-0.5 text-[10px] text-fg-3">{sub}</div>
    </div>
    <div className="shrink-0 self-center text-right"><div className="num text-[12px] text-fg-2">{fmtInt(count)}x</div></div>
    <span className="shrink-0 self-center"><Badge tone={tone}>{status}</Badge></span>
  </div>
);

const MAX_TILES = 80;
const Side = ({ lines, tickets, empty }: { lines: TradeCardLine[]; tickets: React.ReactNode; empty: string }) => {
  const [limit, setLimit] = useState(MAX_TILES);
  if (!lines.length && !tickets) return <div className="px-2 py-8 text-center text-[13px] text-fg-3">{empty}</div>;
  return (
    <div className="grid gap-1.5">
      {tickets}
      {lines.slice(0, limit).map((l, i) => <CardTile key={`${l.cardId}:${i}`} l={l} />)}
      {lines.length > limit && (
        <button type="button" onClick={() => setLimit((n) => n + 200)} className="w-full cursor-pointer rounded-lg border border-dashed border-soft bg-transparent py-1.5 text-[12px] text-fg-3 hover:border-mid hover:text-fg-1">
          Show {fmtInt(lines.length - limit)} more
        </button>
      )}
    </div>
  );
};

/**
 * The centrepiece: the trade rebuilt like the MTGO trade window. Left is what
 * we took (their cards, or the tickets they paid us); right is what they took
 * (our cards, or the tickets we paid out). Their untargeted binder is a count:
 * binder contents are never stored, only what we wanted.
 */
const TradeWindow = ({ insp }: { insp: Inspection }) => {
  const { row } = insp;
  const bought = insp.theyTook.filter((l) => l.status === 'bought').length;
  const weBought = insp.weTook.filter((l) => l.status === 'bought').length;
  const hasBook = [...insp.weTook, ...insp.theyTook].some((l) => l.bookPrice);
  return (
    <Panel flush>
      <div className="grid grid-cols-[1fr_auto_1fr] items-center gap-3 border-b border-soft bg-page-alt px-4 py-2.5">
        <div className="eyebrow truncate text-fg-3" title="What we took from the trader. Green: wanted and bought. Amber: wanted but not bought.">We took</div>
        <Icon icon="switchAccess" size={15} className="text-fg-3" />
        <div className="eyebrow truncate text-right text-fg-3" title="What the trader took from us. Green: bought. Amber: added but not bought.">They took</div>
      </div>
      <div className="grid grid-cols-1 divide-soft md:grid-cols-2 md:divide-x">
        <div className="p-3">
          <Side
            lines={insp.weTook}
            tickets={row.tixIn > 0 ? <TicketTile count={row.tixIn} sub="tix they paid us" status="took in" tone="green" /> : null}
            empty="We took nothing."
          />
          {row.skippedCount > 0 && (
            <div className="mt-1.5 flex items-center justify-between rounded-lg border border-soft px-3 py-2 text-[12px] text-fg-3">
              <span>{fmtInt(row.skippedCount)} cards we don&rsquo;t target</span>
              <span className="num text-[11px]">binder contents not stored</span>
            </div>
          )}
        </div>
        <div className="border-t border-soft p-3 md:border-t-0">
          <Side
            lines={insp.theyTook}
            tickets={row.tixOut > 0 ? <TicketTile count={row.tixOut} sub={row.creditPayout ? 'saved credit cashed out' : 'tix we paid out'} status={row.creditPayout ? 'credit payout' : 'paid out'} tone={row.creditPayout ? 'gold' : 'amber'} /> : null}
            empty="They took nothing."
          />
        </div>
      </div>
      <div className="grid grid-cols-2 divide-x divide-soft border-t border-soft bg-page-alt">
        <div className="px-4 py-2.5">
          <span className="num text-[16px] font-bold text-fg-1">{fmtTix(row.tixOut)}</span>
          <span className="ml-2 text-[12px] text-fg-3">we paid · {fmtInt(weBought)} bought of {fmtInt(row.wantedCount)} wanted of {fmtInt(row.binderCount)}</span>
        </div>
        <div className="px-4 py-2.5 text-right">
          <span className="mr-2 text-[12px] text-fg-3">they paid · {fmtInt(bought)}/{fmtInt(insp.theyTook.length)} bought</span>
          <span className="num text-[16px] font-bold text-fg-1">{fmtTix(row.tixIn)}</span>
        </div>
      </div>
      {hasBook && <div className="border-t border-soft px-4 py-1.5 text-[11px] text-fg-3">* our book price today, not a filed price: the trade never closed on that line.</div>}
    </Panel>
  );
};

const Stepper = ({ stages, tone }: { stages: Inspection['stages']; tone: BadgeTone }) => {
  const reached = stages.filter((s) => s.done).length;
  const dropIndex = reached < stages.length ? reached : -1;
  const drop = { green: 'text-primary border-primary', amber: 'text-warn border-warn', red: 'text-sell border-sell', blue: 'text-info border-info', gold: 'text-gold border-gold', neutral: 'text-fg-3 border-mid' }[tone];
  return (
    <Panel>
      <div className="flex min-w-[520px] items-start overflow-x-auto">
        {stages.map((st, i) => {
          const isDrop = i === dropIndex;
          return (
            <div key={st.label} className="relative flex flex-1 flex-col items-center">
              {i > 0 && <div className={`absolute left-[-50%] right-[50%] top-[11px] z-0 h-[3px] ${st.done ? 'bg-primary' : isDrop ? 'bg-current opacity-40' : 'bg-soft'}`} />}
              <div className={`relative z-10 flex h-6 w-6 items-center justify-center rounded-full border-2 ${st.done ? 'border-primary bg-primary text-on-primary' : isDrop ? `bg-panel ${drop}` : 'border-mid bg-panel text-fg-3'}`}>
                {st.done ? <Icon icon="check" size={13} /> : isDrop ? <Icon icon="close" size={13} /> : null}
              </div>
              <div className="mt-2 px-1 text-center">
                <div className={`text-[12px] font-semibold leading-tight ${st.done ? 'text-fg-1' : isDrop ? drop.split(' ')[0] : 'text-fg-3'}`}>{st.label}</div>
                <div className="num mt-0.5 text-[11px] text-fg-3">{st.at ? hhmmss(st.at) : isDrop ? 'dropped here' : ''}</div>
              </div>
            </div>
          );
        })}
      </div>
    </Panel>
  );
};

const Flags = ({ insp }: { insp: Inspection }) => {
  const r = insp.row;
  const items = [
    { on: r.addedCardsToBuy, label: 'Picked cards to buy', hint: 'added our cards to their side' },
    { on: r.botMadeOffer, label: 'Bot made an offer', hint: 'we offered to buy their cards' },
    { on: r.botWantedNothing, label: 'Nothing to buy', hint: 'we wanted none of their cards' },
    { on: r.reachedConfirm, label: 'Reached confirm', hint: 'got to the double-check screen' },
    { on: r.completed, label: 'Completed', hint: 'trade finalised' },
  ];
  return (
    <Panel eyebrow="What happened" title="The raw flags behind the outcome" actions={
      <div className="flex flex-wrap items-center gap-1.5">
        {r.ticketShortfall && <Badge tone={r.shortfallResolved ? 'amber' : 'red'}>{r.shortfallResolved ? 'was short of tickets, replenished' : 'accepted while short of tickets'}</Badge>}
        <Badge tone="neutral">mode {r.mode}</Badge>
        {insp.creditScope && <Badge tone="neutral">credit pot {insp.creditScope}</Badge>}
      </div>
    }>
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
        {items.map((it) => (
          <div key={it.label} className="flex items-start gap-2">
            <span className={`mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded-full ${it.on ? 'bg-primary text-on-primary' : 'border border-soft bg-page text-fg-3'}`}>
              {it.on ? <Icon icon="check" size={11} /> : <span className="h-px w-1.5 bg-current" />}
            </span>
            <div className="min-w-0">
              <div className={`text-[12px] font-semibold leading-tight ${it.on ? 'text-fg-1' : 'text-fg-3'}`}>{it.label}</div>
              <div className="mt-0.5 text-[10.5px] leading-snug text-fg-3">{it.hint}</div>
            </div>
          </div>
        ))}
      </div>
      {(r.reason || insp.ad || insp.partnerBotSignals.length > 0) && (
        <div className="mt-3 flex flex-col gap-1.5 border-t border-soft pt-3 text-[12px] text-fg-3">
          {r.reason && <div>Ended: <span className="text-fg-2">{r.reason}</span></div>}
          {insp.partnerBotSignals.length > 0 && <div>Bot signals: <span className="num text-fg-2">{insp.partnerBotSignals.join(', ')}</span></div>}
          {insp.ad && (
            <div className="min-w-0">
              Advertised at {hhmm(insp.ad.postedAt)} ({insp.ad.source}):
              <div className="num mt-1 overflow-x-auto whitespace-pre rounded border border-soft bg-page-alt px-2 py-1 text-[11px] text-fg-2">{insp.ad.text}</div>
            </div>
          )}
        </div>
      )}
    </Panel>
  );
};

const HistoryPanel = ({ current, partner, rows, total, onOpen }: { current: string; partner: string; rows: TradeSessionRow[]; total: number; onOpen: (id: string) => void }) => (
  <Panel flush eyebrow="Timeline" title={`${partner}’s trades with us`} actions={<span className="num text-[12px] text-fg-3">{fmtInt(total)} trades{total > rows.length ? `, newest ${fmtInt(rows.length)}` : ''}</span>}>
    <div className="overflow-x-auto px-2 py-2">
      <table className="w-full border-collapse text-[13px]">
        <thead>
          <tr>
            {['When', 'Bot', 'Kind', 'Direction', 'Binder', 'Took', 'Tix'].map((h, i) => (
              <th key={h} className={`px-2 py-2 text-[11px] font-semibold text-fg-3 ${i >= 4 ? 'text-right' : 'text-left'}`}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((o) => {
            const k = kindVocab(o.kind);
            const moved = o.tixIn + o.tixOut;
            const isThis = o._id === current;
            return (
              <tr key={o._id} onClick={() => !isThis && onOpen(o._id)} className={`border-t border-soft/60 ${isThis ? 'bg-page-alt' : 'cursor-pointer hover:bg-page-alt'}`}>
                <td className="num whitespace-nowrap px-2 py-2 text-[12px]"><span className="text-fg-2">{ageLabel(o.at)}</span> <span className="text-fg-3">{hhmm(o.at)}</span>{isThis && <span className="ml-1.5 text-[10px] font-bold text-primary">THIS</span>}</td>
                <td className="num px-2 py-2 text-[12px] text-fg-2">{o.bot}</td>
                <td className="px-2 py-2"><Badge tone={k.tone}>{o.creditPayout ? 'Credit payout' : k.label}</Badge></td>
                <td className="px-2 py-2 text-[12px] text-fg-2">{o.direction !== 'none' ? DIR_LABEL[o.direction] : <span className="text-fg-3">·</span>}</td>
                <td className="num px-2 py-2 text-right text-[12px] text-fg-2">{fmtInt(o.binderCount)}</td>
                <td className="num px-2 py-2 text-right text-[12px] text-fg-2">{o.takenCount || '–'}</td>
                <td className="num px-2 py-2 text-right text-[12px] text-fg-1">{moved ? fmtInt(Math.round(moved)) : '–'}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  </Panel>
);
